import { z, customAlphabet, mapBy, camelToSnake, mapKeys, keyBy, keyOrDie } from "@repo/utils";

// keep in sync with custom entityId function in postgres
const idAlphabet = "useandom2619834075px_bfghjklqvwyzrict";
const entityNanoid = customAlphabet(idAlphabet);

export function entityId<T extends string>(prefix: T) {
    return EntityIdSchema(prefix).parse(`${prefix}_${entityNanoid(24)}`);
}

export function EntityIdSchema<T extends string>(prefix: T) {
    return z.templateLiteral([prefix, "_", z.string().check(z.minLength(21), z.maxLength(24))]);
}

export function EntitySchema<T extends z.core.$ZodShape>(shape: T) {
    const map = mapBy(
        Object.keys(shape),
        k => camelToSnake(k),
        k => k,
    );
    const schema = z.pipe(
        z.pipe(
            z.looseObject({}),
            z.transform(x => mapKeys(x, k => map.get(k) ?? k)),
        ),
        z.object(shape),
    );
    return Object.assign(schema, {
        pick<K extends Partial<Record<keyof T, true | string>>>(keys: K) {
            return EntitySchema(
                keyBy(
                    Object.entries(keys),
                    ([k, v]) => (v === true ? k : (v as string)),
                    ([k]) => keyOrDie(shape, k),
                ) as unknown as {
                    [P in keyof K as K[P] extends true ? P : K[P] & string]: T[P & keyof T];
                },
            );
        },
        extend<T2 extends z.core.$ZodShape>(shape2: T2) {
            return EntitySchema({
                ...shape,
                ...shape2,
            });
        },
        shape,
    });
}
